import { ChangeEventHandler, MouseEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import ModalV2 from '../../shared/ModalV2';
import CopyTag from '../CopyTag/CopyTag';
import { TagsHeader } from '../TagsHeader/TagsHeader';
import { TagsTable } from '../TagsTable/TagsTable';
import { Loading, Modal, Section } from '../../shared';

import { useDeleteTag, useTags } from '../../hooks/tags';

import { handleError } from '../../utils/utils.ts';

import { RouteTo } from '../../routes';

import { Tag } from '../../types/tagTypes';

const TAGS_SEARCH_STORAGE_KEY = 'tagsSearchString';

function filterTagsBySearchString(tags: Tag[], searchString: string) {
    const normalizedSearchString = searchString.trim().toLowerCase();

    if (!normalizedSearchString) {
        return tags;
    }

    return tags.filter((tag) => {
        const searchFields = [
            tag.stringId,
            ...Object.values(tag.name).filter(Boolean),
        ];

        return searchFields.some((value) =>
            value?.toLowerCase().includes(normalizedSearchString),
        );
    });
}

export function Tags() {
    const navigate = useNavigate();
    const { tags, errorMessage, isLoading } = useTags();
    const { deleteTag: deleteTagMutation } = useDeleteTag();
    const [searchString, setSearchString] = useState(
        localStorage.getItem(TAGS_SEARCH_STORAGE_KEY) ?? '',
    );
    const [tagIdsToCopy, setTagIdsToCopy] = useState<Tag['id'][]>();
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedTagIds, setSelectedTagIds] = useState<Set<Tag['id']>>(
        () => new Set(),
    );

    const onSearchStringChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value;

        localStorage.setItem(TAGS_SEARCH_STORAGE_KEY, value);
        setSearchString(value);
    };

    const redirectToTagDetails = (id: Tag['id']) => {
        navigate(RouteTo.TagEdit(String(id)));
    };

    const deleteTag = async (
        e: MouseEvent<HTMLButtonElement>,
        tagId: Tag['id'],
    ) => {
        e.stopPropagation();

        const isConfirmed = await Modal.confirm({
            heading: 'Delete tag',
            text: 'Really delete this tag?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        try {
            await deleteTagMutation(tagId);
        } catch (error) {
            handleError('Error deleting tag', error);
        }
    };

    const copyTag = (e: MouseEvent<HTMLButtonElement>, tagId: Tag['id']) => {
        e.stopPropagation();
        setTagIdsToCopy([tagId]);
    };

    const toggleSelectionMode = () => {
        if (selectedTagIds.size > 0) {
            setSelectedTagIds(new Set());
            setIsSelectionMode(false);
            return;
        }
        setIsSelectionMode((prev) => !prev);
    };

    const toggleTagSelection = (id: Tag['id']) => {
        setSelectedTagIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const openCopyMultiple = () => {
        if (selectedTagIds.size === 0) return;
        setTagIdsToCopy(Array.from(selectedTagIds));
    };

    const filteredTags = useMemo(
        () => filterTagsBySearchString(tags, searchString),
        [tags, searchString],
    );

    const visibleTags = useMemo(() => {
        if (!isSelectionMode || selectedTagIds.size === 0) {
            return filteredTags;
        }
        const selectedList = tags.filter((t) => selectedTagIds.has(t.id));
        const rest = filteredTags.filter((t) => !selectedTagIds.has(t.id));
        return [...selectedList, ...rest];
    }, [filteredTags, isSelectionMode, selectedTagIds, tags]);

    if (isLoading && !tags.length) {
        return <Loading />;
    }

    if (errorMessage) {
        return <Section>{errorMessage}</Section>;
    }

    return (
        <Section>
            {tagIdsToCopy !== undefined && (
                <ModalV2>
                    <CopyTag
                        tagIds={tagIdsToCopy}
                        onClose={() => setTagIdsToCopy(undefined)}
                    />
                </ModalV2>
            )}
            <TagsHeader
                tagsCount={filteredTags.length}
                searchString={searchString}
                onSearchStringChange={onSearchStringChange}
                isSelectionMode={isSelectionMode}
                selectedCount={selectedTagIds.size}
                onToggleSelectionMode={toggleSelectionMode}
                onCopyMultiple={openCopyMultiple}
            />
            {visibleTags.length ? (
                <TagsTable
                    tags={visibleTags}
                    onTagClick={redirectToTagDetails}
                    onDeleteTagClick={deleteTag}
                    onCopyTagClick={copyTag}
                    isSelectionMode={isSelectionMode}
                    selectedTagIds={selectedTagIds}
                    onToggleTagSelection={toggleTagSelection}
                />
            ) : (
                <div>No tags</div>
            )}
        </Section>
    );
}
